import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, saveAuthTokens } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BidoraLogo from "@/components/BidoraLogo";

/**
 * Direct Google OAuth callback handler.
 *
 * Mounted at `/auth/google`. Google redirects the user back here with
 * `?code=<auth_code>&state=<state>` after the user picks their account
 * on accounts.google.com. We POST the code to our backend, which exchanges
 * it with Google for tokens, verifies the id_token, and returns a normal
 * Bidorah JWT session cookie.
 *
 * Also kept compatible with the legacy `/auth/callback#session_id=...`
 * route so any in-flight redirects from previously deployed APKs still
 * complete (they post to /auth/google-session instead).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState("");
  const [nativeReturnPrompt, setNativeReturnPrompt] = useState(false);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const search = typeof window !== "undefined" ? window.location.search : "";
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const query = new URLSearchParams(search);
    const code = query.get("code");
    const state = query.get("state");
    const oauthError = query.get("error");

    // Defense-in-depth: if a fresh service-worker activates mid-flight and
    // forces a reload of this very page (or the user hits refresh), the
    // SAME ?code= would otherwise be re-submitted to /auth/google. Google
    // rejects already-redeemed codes → 401 → red "Sign-in failed" screen,
    // even though the *first* exchange succeeded. We persist a fingerprint
    // of the code in sessionStorage so a reload short-circuits straight to
    // the dashboard instead of refiring the API call.
    try {
      if (code) {
        const spentKey = `bidorah.google.oauth.spent.${code.slice(-12)}`;
        if (sessionStorage.getItem(spentKey)) {
          window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { replace: true });
          return;
        }
        sessionStorage.setItem(spentKey, "1");
      }
    } catch { /* sessionStorage disabled — ignore */ }

    // ── Legacy Emergent-bounce fallback (#session_id=...). Drops away once
    // every cached client has migrated to the new direct flow.
    const fragment = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = fragment.get("session_id");

    if (oauthError) {
      setError("Google sign-in was cancelled.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
      return;
    }

    if (!code && !sessionId) {
      setError("Missing Google response. Please try again.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
      return;
    }

    // Anti-CSRF: state we stashed in sessionStorage MUST match what Google
    // sent back. If sessionStorage was cleared (e.g., different tab) we let
    // it through — the backend still validates the auth code against our
    // client secret with Google.
    if (code) {
      try {
        const stashed = sessionStorage.getItem("bidorah.google.oauth.state");
        sessionStorage.removeItem("bidorah.google.oauth.state");
        // Compare with stripped "native:" prefix on either side so a
        // standalone-browser callback isn't treated as a CSRF mismatch
        // just because OAuth was started inside the APK.
        const stripPrefix = (s) => (typeof s === "string" ? s.replace(/^native:/, "") : s);
        if (stashed && state && stripPrefix(stashed) !== stripPrefix(state)) {
          setError("Sign-in state mismatch. Please try again.");
          setTimeout(() => navigate("/login", { replace: true }), 1800);
          return;
        }
      } catch { /* sessionStorage disabled — ignore */ }
    }

    // ── Native OAuth bounce ────────────────────────────────────────
    // If OAuth was started inside the APK we tagged state with "native:".
    // When we're running inside the APK's own WebView (isCapacitor=true)
    // we just complete the sign-in here — cookies will land on the
    // WebView's bidorah.com origin and the user is signed in.
    //
    // When we're running in standalone Chrome (Custom Tab opened by the
    // APK), Android's verified App Link handler will intercept this
    // exact URL and route it into the APK — the APK's AuthCallback
    // then redeems the code. We MUST NOT redeem the code here in
    // Chrome, or we'll race the APK, consume the one-time code first,
    // and leave the APK staring at a 401 (invalid_grant → red
    // "Sign-in failed" screen).
    //
    // We also try the `com.bidorah.app://` custom-scheme redirect as a
    // belt-and-suspenders bounce back into the app for phones where
    // App Links aren't verified (Play Signing SHA mismatch etc.). Then
    // we stop — the APK is guaranteed installed because the OAuth was
    // started from it in the first place (state `native:` prefix only
    // gets set inside the APK).
    if (code && typeof state === "string" && state.startsWith("native:")) {
      let isCapacitor = false;
      try { isCapacitor = !!(window?.Capacitor?.isNativePlatform?.()); } catch (_) {}
      if (!isCapacitor) {
        const params = new URLSearchParams(window.location.search).toString();
        try { window.location.replace(`com.bidorah.app://auth/google?${params}`); } catch (_) {}
        // Stop here. Do NOT call the API from Chrome — the APK will.
        // Show a clear prompt so the user knows to return to the app
        // (Chrome may or may not close depending on Android version /
        // App Link verification state).
        setNativeReturnPrompt(true);
        // Best-effort: try to close this tab if Chrome allows it.
        setTimeout(() => { try { window.close(); } catch (_) {} }, 1500);
        return;
      }
    }

    (async () => {
      try {
        let data;
        if (code) {
          const redirectUri = `${window.location.origin}/auth/google`;
          ({ data } = await api.post("/auth/google", { code, redirect_uri: redirectUri }));
        } else {
          ({ data } = await api.post("/auth/google-session", { session_id: sessionId }));
        }
        setUser(data);
        // Persist tokens for native/mobile auth (see lib/api.js notes).
        saveAuthTokens(data);
        toast.success(data.is_new ? `Welcome to Bidorah, ${data.name}!` : `Welcome back, ${data.name}`);
        // Strip the query/hash so a page refresh doesn't re-trigger the
        // callback with a now-spent code.
        if (typeof window !== "undefined" && window.history?.replaceState) {
          window.history.replaceState(null, "", "/dashboard");
        }
        navigate(data.is_new ? "/verify" : "/dashboard", { replace: true });
      } catch (e) {
        setError(apiError(e) || "Couldn't complete Google sign-in.");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div
      className="min-h-screen bg-white grid place-items-center px-6"
      data-testid="auth-callback-page"
    >
      <div className="text-center max-w-sm">
        <div className="flex justify-center mb-6">
          <BidoraLogo size={36} />
        </div>
        {error ? (
          <>
            <p className="font-display font-black text-xl tracking-tight text-red-600">
              Sign-in failed
            </p>
            <p className="text-sm text-zinc-600 mt-2">{error}</p>
            <p className="text-xs text-zinc-400 mt-4">Redirecting to login…</p>
          </>
        ) : nativeReturnPrompt ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-green-100 grid place-items-center mb-4">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4 12l5 5L20 6" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="font-display font-black text-xl tracking-tight text-zinc-900">
              You&apos;re signed in!
            </p>
            <p className="text-sm text-zinc-600 mt-2">
              Return to the Bidorah app to continue — you&apos;re already signed in there.
            </p>
            <p className="text-xs text-zinc-400 mt-4">
              You can close this browser tab.
            </p>
          </>
        ) : (
          <>
            <div
              className="mx-auto h-10 w-10 rounded-full border-4 border-zinc-200 border-t-[#1D78AB] animate-spin"
              aria-label="Signing you in"
            />
            <p className="font-display font-black text-xl tracking-tight mt-5">
              Signing you in…
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              Confirming your Google account
            </p>
          </>
        )}
      </div>
    </div>
  );
}
